import React, { useEffect, useMemo, useRef } from 'react';
import { Alert, Spin } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PrintPreviewChrome } from '@/components/print/PrintPreviewChrome';
import ReceivingDimensionalInspectionPrintView, {
  type ReceivingDimensionalPrintRecord,
} from '@/components/quality/ReceivingDimensionalInspectionPrintView';
import { qualityService } from '@/services/quality.service';
import { useSystemSettings } from '@/hooks/useSettings';
import { approvalService } from '@/services/approval.service';
import { ENTITY_TYPE_RECEIVING_DIMENSIONAL_INSPECTION } from '@/constants/approval-entity-types';

const ReceivingDimensionalInspectionPrintPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: settings = [] } = useSystemSettings();
  const didTriggerPrint = useRef(false);

  const { data: record, isLoading, isError, error } = useQuery({
    queryKey: ['receiving-dimensional-print', id],
    queryFn: async () => {
      if (!id) throw new Error('Missing id');
      return (await qualityService.getInspectionById(id)) as ReceivingDimensionalPrintRecord;
    },
    enabled: !!id,
  });

  const { data: approvalSigners = [], isFetched: signersFetched } = useQuery({
    queryKey: ['receiving-dimensional-print-signers', id],
    queryFn: async () => {
      if (!id) return [];
      try {
        return await approvalService.getPrintSigners(
          ENTITY_TYPE_RECEIVING_DIMENSIONAL_INSPECTION,
          id
        );
      } catch {
        return [];
      }
    },
    enabled: !!id,
  });

  const companySettings = useMemo(
    () => settings.filter((setting) => setting.category === 'company'),
    [settings]
  );

  const companyInfo = useMemo(() => {
    const getSetting = (key: string) =>
      companySettings.find((setting) => setting.setting_key === key)?.setting_value?.trim() ?? '';
    const name = getSetting('company_name') || 'Company Name';
    return {
      name,
      legalName: getSetting('company_legal_name') || name,
      address: [
        getSetting('company_address'),
        getSetting('company_city'),
        getSetting('company_country'),
      ]
        .filter(Boolean)
        .join(', '),
    };
  }, [companySettings]);

  useEffect(() => {
    if (!record || !signersFetched || didTriggerPrint.current) return;
    didTriggerPrint.current = true;
    const t = window.setTimeout(() => window.print(), 400);
    return () => window.clearTimeout(t);
  }, [record, signersFetched]);

  if (isLoading) return <Spin style={{ display: 'block', margin: '48px auto' }} />;
  if (isError || !record) {
    return (
      <Alert
        type="error"
        showIcon
        style={{ margin: 24 }}
        message="Unable to load Receiving Dimension Inspection for print"
        description={(error as Error)?.message}
      />
    );
  }

  return (
    <PrintPreviewChrome
      title={`Receiving Dimension ${record.inspection_number}`}
      onBack={() => navigate(`/qms/receiving-dimensional-inspections/${id}`)}
    >
      <ReceivingDimensionalInspectionPrintView
        record={record}
        companyInfo={companyInfo}
        approvalSigners={approvalSigners}
      />
    </PrintPreviewChrome>
  );
};

export default ReceivingDimensionalInspectionPrintPage;
